import { motion } from 'framer-motion'
import { ArrowUpRight } from 'lucide-react'
import React from 'react'
import { useNavigate } from 'react-router'
import { fadeIn, prefersReducedMotion } from '#app/utils/animations'
import { useOptionalUser } from '#app/utils/user'
import { AnimatedButton } from '../ui/button'

interface HeroSectionProps {
	title: string | string[]
	buttonText: string
	backgroundImage: string
	onButtonClick?: () => void
}

export default function HeroSection({
	title = "The Last, Rental Application You'll Ever Fill Out",
	buttonText = 'Start Screening',
	backgroundImage = '/img/herobanner.png',
	onButtonClick,
}: HeroSectionProps) {
	const shouldAnimate = !prefersReducedMotion()
	const navigate = useNavigate()
	const user = useOptionalUser()

	const handleStartScreening =
		onButtonClick ??
		(() => {
			// If user is authenticated, navigate directly to screening
			if (user) {
				void navigate('/tenant/screening')
			} else {
				// If not authenticated, redirect to login with redirectTo parameter
				const redirectTo = encodeURIComponent('/tenant/screening')
				void navigate(`/tenant/login?redirectTo=${redirectTo}`)
			}
		})

	return (
		<section className="relative w-full">
			{/* Mobile & Tablet: Content first, then image */}
			<div className="px-4 md:px-8 flex flex-col lg:hidden">
				{/* Content - centered on mobile/tablet */}
				<motion.div
					className="text-center mb-6 px-4"
					initial={shouldAnimate ? 'hidden' : false}
					animate={shouldAnimate ? 'visible' : false}
					variants={shouldAnimate ? fadeIn : undefined}
					transition={{ delay: 0.2, duration: 0.6 }}
				>
					<motion.h1
						className="text-[25px] mt-[20px] md:mt-0 md:text-5xl leading-tight font-semibold text-[#32302F]"
						dangerouslySetInnerHTML={{ __html: title }}
						initial={shouldAnimate ? { opacity: 0, y: 30 } : false}
						animate={shouldAnimate ? { opacity: 1, y: 0 } : false}
						transition={{
							delay: 0.4,
							duration: 0.7,
							ease: [0.22, 1, 0.36, 1],
						}}
					/>

					<motion.div
						className="flex justify-center mt-6"
						initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
						animate={shouldAnimate ? { opacity: 1, y: 0 } : false}
						transition={{
							delay: 0.6,
							duration: 0.6,
							ease: [0.22, 1, 0.36, 1],
						}}
					>
						<AnimatedButton
							animateOnMount
							entranceDelay={0.6}
							className="rounded-full bg-black px-12 text-white hover:bg-gray-900"
							onClick={handleStartScreening}
						>
							{buttonText}
							<ArrowUpRight size={16} />
						</AnimatedButton>
					</motion.div>
				</motion.div>

				{/* Image - full width on mobile/tablet, breaking out of container */}
				<motion.div
					className="w-full bg-no-repeat rounded-[20px]"
					style={{
						backgroundImage: `url('${backgroundImage}')`,
						aspectRatio: '16/9',
						minHeight: '220px',
						backgroundSize: 'cover',
						backgroundPosition: 'left center',
					}}
					initial={shouldAnimate ? { opacity: 0, scale: 1.05 } : false}
					animate={shouldAnimate ? { opacity: 1, scale: 1 } : false}
					transition={{
						delay: 0.8,
						duration: 0.8,
						ease: [0.22, 1, 0.36, 1],
					}}
				/>
			</div>

			{/* Desktop: Full screen hero */}
			<section className="relative hidden lg:block h-screen overflow-hidden">
				{/* 100px inset frame */}
				<div className="absolute inset-y-0 left-[100px] right-[100px] overflow-hidden rounded-[40px]">
					{/* Background Image (STRETCH ALLOWED) */}
					{/* <motion.img
						src={backgroundImage}
						alt="Hero Background"
						className="
    absolute inset-0
    w-full h-full
    object-cover
    object-[left_top]
  "
						initial={shouldAnimate ? { opacity: 0, scale: 1.05 } : false}
						animate={shouldAnimate ? { opacity: 1, scale: 1.05 } : false}
						transition={{
							delay: 0.3,
							duration: 1,
							ease: [0.22, 1, 0.36, 1],
						}}
					/> */}
					{/* Background Image (ROUNDED + SAFE ZOOM) */}
					<motion.img
						src={backgroundImage}
						alt="Hero Background"
						className="absolute inset-0 w-full h-full object-cover object-[left_top]"
						initial={shouldAnimate ? { opacity: 0 } : false}
						animate={shouldAnimate ? { opacity: 1 } : false}
						transition={{
							delay: 0.3,
							duration: 1,
							ease: [0.22, 1, 0.36, 1],
						}}
					/>

					{/* Content Overlay (same frame) */}
					<motion.div
						className="absolute top-24 left-2 z-10 max-w-md"
						initial={shouldAnimate ? 'hidden' : false}
						animate={shouldAnimate ? 'visible' : false}
						variants={shouldAnimate ? fadeIn : undefined}
						transition={{ delay: 0.2, duration: 0.6 }}
					>
						<motion.div
							className="text-[42px] leading-[1.2] font-semibold text-[#1a1a1a] mb-6"
							dangerouslySetInnerHTML={{ __html: title }}
						/>

						<AnimatedButton
							animateOnMount
							entranceDelay={0.6}
							className="rounded-full bg-[#1a1a1a] text-white hover:bg-black px-6 py-2.5 text-sm font-medium"
							onClick={handleStartScreening}
						>
							{buttonText}
							<ArrowUpRight size={14} className="ml-1" />
						</AnimatedButton>
					</motion.div>
				</div>
			</section>
		</section>
	)
}
